import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  TextField,
  Alert,
} from '@mui/material';
import { Schedule, CheckCircle, Edit } from '@mui/icons-material';
import { CaretakerProfile } from '../services/caretakerProfileService';
import { availabilityService, AvailabilitySlot, AvailabilitySummary } from '../services/availabilityService';

interface CaretakerAvailabilityProps {
  caretakerId: string;
  availability: CaretakerProfile['availability'];
  isOwner?: boolean;
  onSetAvailability?: () => void;
}

const CaretakerAvailability: React.FC<CaretakerAvailabilityProps> = ({
  caretakerId,
  availability,
  isOwner = false,
  onSetAvailability,
}) => {
  const daysOfWeek = [
    'MONDAY',
    'TUESDAY', 
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
    'SUNDAY'
  ];

  const dayNames = [
    'Monday',
    'Tuesday',
    'Wednesday', 
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
  ];

  const getAvailabilityForDay = (day: string) => {
    return availability.find(avail => avail.dayOfWeek === day);
  };

  const isCurrentlyAvailable = (): boolean => {
    const now = new Date();
    const currentDay = daysOfWeek[now.getDay() === 0 ? 6 : now.getDay() - 1]; // Convert Sunday=0 to Sunday=6
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    
    const todayAvailability = getAvailabilityForDay(currentDay);
    
    if (!todayAvailability) return false;
    
    return currentTime >= todayAvailability.startTime && currentTime <= todayAvailability.endTime;
  };

  const toIsoDate = (date: Date): string => date.toISOString().slice(0, 10);
  const [selectedDate, setSelectedDate] = useState(toIsoDate(new Date()));
  const [dateSummary, setDateSummary] = useState<AvailabilitySummary | null>(null);
  const [dateSlots, setDateSlots] = useState<AvailabilitySlot[]>([]);
  const [dateLookupError, setDateLookupError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const authToken = localStorage.getItem('authToken');

    if (!authToken || isOwner || !caretakerId) {
      return undefined;
    }

    setDateLookupError(null);

    Promise.all([
      availabilityService.getSummary(caretakerId, selectedDate),
      availabilityService.getSlots(caretakerId, selectedDate),
    ])
      .then(([summary, slots]) => {
        if (!mounted) return;
        setDateSummary(summary);
        setDateSlots(slots);
      })
      .catch(() => {
        if (!mounted) return;
        setDateSummary(null);
        setDateSlots([]);
        setDateLookupError('Unable to load date-specific availability right now.');
      });

    return () => {
      mounted = false;
    };
  }, [caretakerId, isOwner, selectedDate]);

  const selectedDateAvailabilityLabel = useMemo(() => {
    if (!dateSummary) return null;
    return dateSummary.isAvailableOnDate
      ? `Available on ${selectedDate}`
      : `Unavailable on ${selectedDate}`;
  }, [dateSummary, selectedDate]);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Schedule />
            <Typography variant="h5" component="h2">
              Availability
            </Typography>
            {isCurrentlyAvailable() && (
              <Chip
                icon={<CheckCircle />}
                label="Available Now"
                color="success"
                size="small"
              />
            )}
          </Box>
          {isOwner && onSetAvailability && (
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={onSetAvailability}
              size="small"
            >
              Set Schedule
            </Button>
          )}
        </Box>
        
        {availability.length > 0 ? (
          <>
            {!isOwner && (
              <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField
                  label="Check a specific date"
                  type="date"
                  size="small"
                  value={selectedDate}
                  onChange={(event) => setSelectedDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                {selectedDateAvailabilityLabel && (
                  <Chip
                    label={selectedDateAvailabilityLabel}
                    color={dateSummary?.isAvailableOnDate ? 'success' : 'default'}
                    sx={{ width: 'fit-content' }}
                  />
                )}
                {dateLookupError && <Alert severity="warning">{dateLookupError}</Alert>}
                {dateSlots.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Time windows: {dateSlots.map((slot) => `${slot.startTime}-${slot.endTime}`).join(', ')}
                  </Typography>
                )}
              </Box>
            )}

            <Grid container spacing={2}>
              {daysOfWeek.map((day, index) => {
                const dayAvailability = getAvailabilityForDay(day);
                const isToday = new Date().getDay() === (index === 6 ? 0 : index + 1);

                return (
                  <Grid item xs={12} sm={6} md={4} key={day}>
                    <Box
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: dayAvailability ? 'success.main' : 'divider',
                        borderRadius: 1,
                        backgroundColor: isToday ? 'action.hover' : 'transparent',
                        borderWidth: isToday ? 2 : 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={isToday ? 'bold' : 'normal'}
                        color={isToday ? 'primary' : 'text.primary'}
                        gutterBottom
                      >
                        {dayNames[index]}
                        {isToday && ' (Today)'}
                      </Typography>

                      {dayAvailability ? (
                        <Typography variant="body2" color="success.main">
                          {dayAvailability.startTime} - {dayAvailability.endTime}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Not available
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </>
        ) : (
          <Typography variant="body1" color="text.secondary">
            Availability schedule not set.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default CaretakerAvailability;
